import { navigate } from "gatsby";

import create from "zustand";

import { errorAwareApiFetch } from "../../../../apiBindings";
import pushToAnalytics from "../../../../utils/analytics";
import {
    storageLocal as storage,
    storageSession as session,
} from "../../../../utils/StorageHandler";
import iCheckoutData from "../interfaces/iCheckoutData";

interface iHeatingOilCheckout {
    checkoutData: iCheckoutData;
    climateNeutral: boolean;
    detailsChanged: number;
    editMode: boolean;
    overlayContent: string;
    initCheckout: (
        zipcode: string,
        amount: number,
        up: number,
        notify: (
            title: string,
            message: string,
            type?: string,
            userOptions?: object
        ) => void
    ) => Promise<iCheckoutData>;
    setCheckoutData: (checkoutData: iCheckoutData, setSession: boolean) => void;
    getPrices: () => void;
    clearCheckout: () => void;
    setClimateNeutral: (climateNeutral: boolean) => void;
    detailsHasChanged: () => void;
    setOverlayContent: (overlayContent: string) => void;
}

const useStore = create<iHeatingOilCheckout>((set, get) => ({
    productIsPremium: false,
    checkoutData: session.getJson("hel:checkout", {
        user_hash: "",
        zipcode: "",
        amount: 0,
        unloading_points: "",
        product_id: 0,
        base_price: 0,
        price_action_save: 0,
        price_net: "",
        price_gross: "",
        price_total_net: "",
        price_total_gross: "",
        price_gross_with_action: null,
        price_total_gross_with_action: null,
        is_premium: false,
        delivery_time_days: 0,
        lastCheckoutStep: "",
        payment_type_id: 0,
        hose_id: 0,
        climate_neutral: false,
        delivery_time_id: 0,
        ggvs_add: "",
        tax_rate: 0,
        updated_at: "",
        created_at: "",
        additional_addresses: [],
        history_prices: [],
    }),
    climateNeutral: storage.getBoolean("hel:climate-neutral"),
    detailsChanged: 0,
    editMode: false,
    overlayContent: "",

    initCheckout: async (
        zipcode: string,
        amount: number,
        up: number,
        notify
    ): Promise<iCheckoutData> => {
        const product = storage.get("hel:product-id");
        try {
            const { data } = await errorAwareApiFetch<{ data: any }>(
                `/heatingoil/checkout/init/${zipcode}/${amount}/${up}/${product}`
            );
            session.setJson("hel:checkout", data);
            storage.set("hel:needs-session", true);
            set({ checkoutData: data });

            if (data.climate_neutral === true) {
                storage.set("hel:climate-neutral", true);
                set({ climateNeutral: true });
            }

            // Send a checkout start event to google tag manager
            pushToAnalytics("hel:checkout-start", {});

            return data;
        } catch ({ data }) {
            notify("Fehlgeschlagen", (data as any).message);
            // Abort if no payment method is available
            if ((data as any)?.type === "HEL_INIT_NO_AVAILABLE_PAYMENT_TYPE") {
                void navigate("/heizoel");
            }
            return get().checkoutData;
        }
    },

    setCheckoutData: (checkoutData, setSession = false) => {
        set({ checkoutData: checkoutData });
        if (setSession) {
            session.setJson("hel:checkout", checkoutData);
        }
    },

    getPrices: () => {
        const checkoutData = get().checkoutData;
        if (!checkoutData) {
            return null;
        }

        return {
            priceNet: checkoutData.price_net,
            priceGross: checkoutData.price_gross,
            priceTotalNet: checkoutData.price_total_net,
            priceTotalGross: checkoutData.price_total_gross,
            priceWithAction: checkoutData.price_gross_with_action,
            priceTotalWithAction: checkoutData.price_total_gross_with_action,
            priceActionSave: checkoutData.price_action_save,
        };
    },

    setClimateNeutral: (climateNeutral: boolean) => {
        set({ climateNeutral: climateNeutral });
    },

    detailsHasChanged: () => {
        set({ detailsChanged: get().detailsChanged + 1 });
    },

    setOverlayContent: (overlayContent: string) => {
        set({ overlayContent: overlayContent });
    },

    clearCheckout: () => {
        set({
            checkoutData: {
                user_hash: "",
                zipcode: "",
                amount: 0,
                unloading_points: "",
                product_id: 0,
                base_price: 0,
                price_action_save: 0,
                price_net: "",
                price_gross: "",
                price_total_net: "",
                price_total_gross: "",
                price_gross_with_action: null,
                price_total_gross_with_action: null,
                is_premium: false,
                delivery_time_days: 0,
                lastCheckoutStep: "",
                payment_type_id: 0,
                hose_id: 0,
                climate_neutral: false,
                delivery_time_id: 0,
                ggvs_add: "",
                tax_rate: 0,
                updated_at: "",
                created_at: "",
                additional_addresses: [],
            },
            detailsChanged: 0,
        });
        storage.remove("hel:delivery-time-type");
        storage.remove("hel:delivery-time-frame");
        storage.remove("hel:delivery-time-wish-date");
        storage.remove("hel:additional-addresses");
        storage.remove("hel:anti-freeze");
        storage.remove("hel:order-comment");
        storage.remove("hel:needs-session");

        session.remove("hel:checkout");
        session.remove("hel:order");
    },
}));

export default useStore;
